'use client'

import { memo, useMemo, useState } from 'react'

import _ from 'lodash'
import { LoaderCircle } from 'lucide-react'

import { LevelFooter } from '../../Footer'
import { HUD } from '../../HUD'
import { LevelToolbar } from '../../Toolbar'

import { useLevelContext } from '@/features/level-play'
import { useFlow } from '@/shared/lib/steps-flow'
import { Button } from '@/shared/ui/Button'

import { WordHuntGame } from './Game'


interface GameWordHuntProps {
  challengeId: number
}

export const GameWordHunt = memo<GameWordHuntProps>(({ challengeId }) => {
  const { continueDisabled, goNext, loseLife, enableContinue } = useFlow()
  const { level, isSubmitting, onChallengeAnswer } = useLevelContext()

  const errorsAllowed = level?.journey?.allow_wrong_answers ?? false
  const [errorCount, setErrorCount] = useState(0)

  const challenge = useMemo(
    () =>
      level?.active_challenges.find(challenge => challenge.id === challengeId),
    [level?.active_challenges, challengeId]
  )

  const words = useMemo(() => {
    const source =
      challenge?.challenge_options?.length
        ? challenge.challenge_options.map(opt => opt.name ?? '')
        : challenge?.meta?.words?.map((w: { name: string }) => w.name ?? '') ??
          []

    return source.map((name: string) =>
      _.truncate(_.deburr(name).toUpperCase().replace(/\s+/g, ''), {
        length: 10,
        omission: ''
      })
    )
  }, [challenge?.challenge_options, challenge?.meta?.words])

  const showWords = useMemo(() => {
    return challenge?.meta?.show_words ?? false
  }, [challenge?.meta?.show_words])

  const handleComplete = async (): Promise<void> => {
    if (!challenge) return

    const { ok } = await onChallengeAnswer({
      uid: challenge.uid,
      answer: `Palavras encontradas: ${words.join(', ')}`,
      skipValidation: true
    })

    if (ok) {
      enableContinue()
    }
  }

  const registerErrorAndEnableContinue = async (): Promise<void> => {
    if (!challenge) return
    const { ok } = await onChallengeAnswer({
      uid: challenge.uid,
      answer: 'erro',
      skipValidation: true
    })
    if (ok) {
      enableContinue()
    }
  }

  const handleError = (): void => {
    const next = errorCount + 1
    setErrorCount(next)

    if (next % 3 === 0) {
      loseLife()
    }

    if (errorsAllowed && next === 1) {
      void registerErrorAndEnableContinue()
    }
  }

  if (!challenge) {
    return (
      <HUD.Card>
        <HUD.Body className="flex items-center justify-center p-8">
          <div className="text-center text-gray-500">
            <p>Desafio não encontrado</p>
          </div>
        </HUD.Body>
      </HUD.Card>
    )
  }

  return (
    <HUD.Card>
      <HUD.Header challenge={challenge} />
      <HUD.Toolbar>
        <LevelToolbar />
      </HUD.Toolbar>
      <HUD.Body className="flex flex-col gap-4">
        <HUD.Description challenge={challenge} />

        <div className="w-full">
          <WordHuntGame
            wordsList={words}
            showWords={challenge.meta?.show_words ?? false}
            onComplete={handleComplete}
            onError={handleError}
          />
        </div>
      </HUD.Body>
      <LevelFooter>
        <Button
          type="button"
          onClick={goNext}
          className="w-full font-bold btn btn-info position-relative z-index-3"
          disabled={continueDisabled || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <LoaderCircle className="size-5 animate-spin" />
              <span>Carregando</span>
            </>
          ) : (
            <span>Continuar</span>
          )}
        </Button>
      </LevelFooter>
    </HUD.Card>
  )
})

GameWordHunt.displayName = 'GameWordHunt'
